import { useState, useEffect } from 'react';
import { Bell, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';

interface Notification {
  id: string;
  type: string;
  review_id: string | null;
  reply_id: string | null;
  post_id: string | null;
  comment_id: string | null;
  actor_id: string;
  is_read: boolean;
  created_at: string;
  actor_name?: string;
  experience_name?: string;
  experience_slug?: string;
}

interface NotificationBellProps {
  userId: string;
}

export const NotificationBell = ({ userId }: NotificationBellProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { unreadCount: unreadMessageCount } = useUnreadMessages(userId);
  
  const totalUnreadCount = unreadNotificationCount + unreadMessageCount;

  const fetchNotifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && data) {
      // Fetch actor names and experience info
      const actorIds = [...new Set(data.map(n => n.actor_id))];
      const reviewIds = [...new Set(data.filter(n => n.review_id).map(n => n.review_id!))];

      const [profilesRes, reviewsRes] = await Promise.all([
        supabase.from('profiles').select('user_id, display_name').in('user_id', actorIds),
        reviewIds.length > 0 
          ? supabase.from('experience_reviews').select('id, experience_id, experiences(name, slug)').in('id', reviewIds)
          : Promise.resolve({ data: [] })
      ]);

      const profileMap = new Map<string, string>(
        profilesRes.data?.map(p => [p.user_id, p.display_name || 'Someone']) || []
      );
      const reviewMap = new Map<string, { name: string; slug: string }>(
        (reviewsRes.data as any[])?.map((r) => [r.id, { name: r.experiences?.name || '', slug: r.experiences?.slug || '' }]) || []
      );

      const enrichedNotifications = data.map(n => ({
        ...n,
        actor_name: profileMap.get(n.actor_id) || 'Someone',
        experience_name: n.review_id ? reviewMap.get(n.review_id)?.name : undefined,
        experience_slug: n.review_id ? reviewMap.get(n.review_id)?.slug : undefined,
      }));

      setNotifications(enrichedNotifications);
      setUnreadNotificationCount(data.filter(n => !n.is_read).length);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to realtime updates for INSERT and UPDATE
    const channel = supabase
      .channel('notifications-' + userId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const markAsRead = async (notificationId: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
    
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadNotificationCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadNotificationCount(0);
  };

  const getNotificationText = (notification: Notification) => {
    const actor = notification.actor_name || 'Someone';
    const experience = notification.experience_name || 'an experience';
    
    switch (notification.type) {
      case 'review_like':
        return `${actor} reacted to your review on ${experience}`;
      case 'review_reply':
        return `${actor} replied to your review on ${experience}`;
      case 'reply_to_reply':
        return `${actor} replied to your comment on ${experience}`;
      case 'mention':
        return `${actor} mentioned you`;
      default:
        return `${actor} interacted with your content`;
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2.5 rounded-full hover:bg-secondary transition-colors">
          <Bell className="w-5 h-5" />
          {totalUnreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-medium">
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 z-[2200] bg-background border border-border rounded-xl shadow-xl" sideOffset={12}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {unreadNotificationCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-xs text-primary hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>
        
        {/* Unread Messages Link */}
        {unreadMessageCount > 0 && (
          <Link
            to="/dashboard?section=messages"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 p-4 border-b border-border bg-primary/5 hover:bg-primary/10 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {unreadMessageCount} unread message{unreadMessageCount > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-muted-foreground">View your messages</p>
            </div>
          </Link>
        )}
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map(notification => (
              <Link
                key={notification.id}
                to={
                  notification.post_id 
                    ? '/activity' 
                    : notification.experience_slug 
                      ? `/experiences/${notification.experience_slug}` 
                      : '/dashboard?section=reviews'
                }
                onClick={() => {
                  if (!notification.is_read) markAsRead(notification.id);
                  setIsOpen(false);
                }}
                className={cn(
                  "block p-4 border-b border-border hover:bg-muted/50 transition-colors",
                  !notification.is_read && "bg-primary/5"
                )}
              >
                <p className="text-sm text-foreground leading-snug">
                  {getNotificationText(notification)}
                </p>
                <span className="text-xs text-muted-foreground mt-1 block">
                  {getTimeAgo(notification.created_at)}
                </span>
              </Link>
            ))
          ) : (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No notifications yet
            </div>
          )}
        </div>
        {notifications.length > 0 && (
          <div className="p-3 border-t border-border">
            <Link
              to="/dashboard?section=reviews"
              onClick={() => setIsOpen(false)}
              className="text-sm text-primary hover:underline block text-center"
            >
              View all reviews
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
